import { MAIN_PHONE } from "@/data/contact";

export type LocationLandingId = "huelva" | "cadiz" | "andalucia";

type HighlightItem = {
  title: string;
  description: string;
};

type StepItem = {
  title: string;
  subtitle: string;
  description: string;
};

type SectionTitle = {
  title: string;
  subtitle: string;
};

type CoverageSection = {
  eyebrow: string;
  title: string;
  titleTag?: "h2" | "h3";
  subtitle: string;
  items: string[];
};

type ReviewsSection = {
  title: string;
  headline: string;
  body: string;
  ratingCaption: string;
  scoreLabel: string;
  countLabel: string;
  bullets: string[];
  cta: string;
};

type CtaBlock = {
  title: string;
  titleTag?: "h3" | "h4" | "h5";
  copy: string;
  primary: string;
  secondary: string;
  imageAlt: string;
};

type AboutSection = {
  eyebrow: string;
  title: string;
  titleTag?: "h2" | "h3";
  copy: string;
  phoneLabel: string;
  phoneNumber: string;
  addressLabel: string;
  address: string;
  imageAlt: string;
};

type HeroSection = {
  eyebrow: string;
  title: string;
  subtitle: string;
  alt: string;
};

type IntroSection = {
  title: string;
  titleTag?: "h2" | "h3";
  subtitle: string;
  extraParagraphs?: string[];
  cta: string;
};

type FormBlock = {
  eyebrow: string;
  title: string;
  subtitle: string;
  sidebarCall: string;
};

type MetaSection = {
  title: string;
  description: string;
};

type ReviewsSummary = {
  ratingLabel: string;
  countLabel: string;
};

export type LocationLandingCopy = {
  meta: MetaSection;
  hero: HeroSection;
  intro: IntroSection;
  about: AboutSection;
  reviews: ReviewsSummary;
  formBlock: FormBlock;
  benefits: SectionTitle;
  highlights: HighlightItem[];
  steps: SectionTitle & { items: StepItem[] };
  coverage: CoverageSection;
  reviewsSection: ReviewsSection;
  ctaBlock: CtaBlock;
};

export type LocationLandingConfig = {
  slug: string;
  phoneHref: string;
  googleReviewsUrl: string;
  heroImage: string;
  aboutImage: string;
  ctaImage: string;
};

export const LOCATION_HIGHLIGHT_ICONS = [
  "/static/icons/valoraciones.webp",
  "/static/icons/manos-unidas.webp",
  "/static/icons/asistente.webp",
  "/static/icons/dinero.webp",
  "/static/icons/mano.webp",
  "/static/icons/bombilla.webp",
];

const LOCATION_LANDING_CONFIGS: Record<LocationLandingId, LocationLandingConfig> = {
  huelva: {
    slug: "/placas-solares-huelva",
    phoneHref: MAIN_PHONE.href,
    googleReviewsUrl: "https://www.google.com/search?q=geesol+rese%C3%B1as",
    heroImage: "/static/services/autoconsumo-domestico/bormujos-sevilla.webp",
    aboutImage: "/static/services/autoconsumo-domestico/alcala-de-guadaira-sevilla.webp",
    ctaImage: "/static/services/autoconsumo-domestico/dos-hermanas-sevilla-01.webp",
  },
  cadiz: {
    slug: "/placas-solares-cadiz",
    phoneHref: MAIN_PHONE.href,
    googleReviewsUrl: "https://www.google.com/search?q=geesol+rese%C3%B1as",
    heroImage: "/static/services/autoconsumo-domestico/dos-hermanas-sevilla-02.webp",
    aboutImage: "/static/services/autoconsumo-domestico/carmona-sevilla.webp",
    ctaImage: "/static/services/autoconsumo-domestico/coria-del-rio-sevilla.webp",
  },
  andalucia: {
    slug: "/subvenciones-placas-solares-andalucia",
    phoneHref: MAIN_PHONE.href,
    googleReviewsUrl: "https://www.google.com/search?q=geesol+rese%C3%B1as",
    heroImage: "/static/services/autoconsumo-domestico/alcala-del-rio-sevilla.webp",
    aboutImage: "/static/services/autoconsumo-domestico/conil-de-la-frontera-cadiz.webp",
    ctaImage: "/static/services/autoconsumo-domestico/el-puerto-de-santa-maria-cadiz-01.webp",
  },
};

export const getLocationLandingConfig = (id: LocationLandingId): LocationLandingConfig =>
  LOCATION_LANDING_CONFIGS[id];

/** Matches next-intl's `t.raw`, scoped to the "locationLandingPages" namespace. */
export type LocationLandingContentReader = (id: LocationLandingId) => LocationLandingCopy;

export const getLocationLandingCopy = (
  id: LocationLandingId,
  readContent: LocationLandingContentReader,
): LocationLandingCopy => readContent(id);
